import { FC } from 'react'
import {
  FormItem,
  GroupedResidentsMultiSelect,
  MultiSelect,
  RangePicker,
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { reportingTranslations } from 'locale/reporting'
import { FormInstance, Select } from 'antd'
import { useSelector } from 'react-redux'
import { Home, LogCategory, LogCategoryItem } from '@lmc/ts-common'
import { filterOption } from './utils'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import useResidentsList from 'hooks/useResidentsList'

type Props = {
  preferredPeopleNominative: string
  isGroupDownload?: boolean
  form?: FormInstance<any>
}

export const LogExportForm: FC<Props> = ({
  preferredPeopleNominative,
  isGroupDownload,
  form,
}) => {
  const { t } = useTranslation(NAMESPACE.REPORTING)

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const { residents } = useResidentsList({})

  const { groupedCarerOptions } = useGroupedCarers()

  const logCategories = useSelector(
    (state: { data: { [`log-categories`]: LogCategory[] } }) =>
      state.data[`log-categories`]
  )

  const logItems = useSelector(
    (state: { data: { [`log-category-items`]: LogCategoryItem[] } }) =>
      state.data[`log-category-items`]
  )

  const homeOptions = homes.map((home) => ({
    value: home?.id,
    label: home?.name,
  }))

  const logItemsOptions = logItems?.map((item) => ({
    value: item?.id,
    label: item?.name,
  }))

  const logCategoryOptions = logCategories?.map((category) => ({
    value: category?.id,
    label: category?.name,
  }))

  return (
    <>
      <FormItem
        label={t(reportingTranslations.LOGS_CREATED)}
        name="dateRange"
        extra={t(reportingTranslations.SELECTED_DATES_logs)}
      >
        <RangePicker />
      </FormItem>
      <FormItem
        label={preferredPeopleNominative}
        name="residents"
        extra={t(reportingTranslations.FILTER_BY_USERS, {
          variable: preferredPeopleNominative,
        })}
      >
        <GroupedResidentsMultiSelect
          residents={residents}
          title={preferredPeopleNominative || ''}
          values={form?.getFieldValue('residents')}
          handleInputChange={(values) =>
            form?.setFieldValue('residents', values)
          }
          placeholder={false}
        />
      </FormItem>
      <FormItem
        label={t(reportingTranslations.TEAM_MEMBERS_TITLE)}
        name="carers"
        extra={t(reportingTranslations.SELECT_ENTITY, {
          variable: t(reportingTranslations.TEAM_MEMBERS_TITLE),
        })}
      >
        <MultiSelect
          title="Team members"
          groupedOptions={groupedCarerOptions}
          hasClear
          hasSearch
          hasSelectAll
          values={form?.getFieldValue('carers')}
          onChange={(values) => form?.setFieldValue('carers', values)}
          placeholder={false}
        />
      </FormItem>
      <FormItem
        label={t(reportingTranslations.LOG_CATEGORY)}
        name="logCategories"
      >
        <Select
          options={logCategoryOptions}
          mode="multiple"
          filterSort={(optionA, optionB) =>
            (optionA?.label ?? '')
              .toLowerCase()
              .localeCompare((optionB?.label ?? '').toLowerCase())
          }
          filterOption={filterOption}
        />
      </FormItem>
      <FormItem
        label={t(reportingTranslations.LOG_ITEMS)}
        name="logCategoryItems"
      >
        <Select
          options={logItemsOptions}
          mode="multiple"
          filterSort={(optionA, optionB) =>
            (optionA?.label ?? '')
              .toLowerCase()
              .localeCompare((optionB?.label ?? '').toLowerCase())
          }
          filterOption={filterOption}
        />
      </FormItem>
      {isGroupDownload && (
        <FormItem
          label={t(reportingTranslations.SITES)}
          name="sites"
          extra={t(reportingTranslations.SITES_EXTRA)}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select options={homeOptions} mode="multiple" />
        </FormItem>
      )}
    </>
  )
}
